import { useAutoResizedTextarea, useTranslations } from "@jugl-web/utils";
import { useAIAssistant } from "@jugl-web/domain-resources/ai-assistant/providers";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { BoardAvatar } from "@jugl-web/domain-resources/tasks";
import {
  InteractiveContainer,
  PlainButton,
  Tooltip,
  ListBoxItem,
  ResourcePickerPopover,
} from "@jugl-web/ui-kit";
import { useTaskBoardList } from "@jugl-web/domain-resources/tasks/hooks/useTaskBoardList";
import isEqual from "lodash/isEqual";
import {
  AIAgent,
  AIAgentType,
} from "@jugl-web/domain-resources/ai-assistant/types";
import { hasRoleAccessToModule } from "@jugl-web/domain-resources/users/utils/hasRoleAccessToModule";
import { AppModule } from "@jugl-web/domain-resources/common/types";
import { ReactComponent as SendIcon } from "./assets/send.svg";
import { ReactComponent as OnbaordingIcon } from "./assets/onboarding.svg";

type AIAgentOption = (
  | { color: string; icon?: never }
  | { icon: React.ReactNode; color?: never }
) & {
  id: string;
  name: string;
  agent: AIAgent;
};

export const AIChatInput: React.FC = () => {
  const { t } = useTranslations();
  const { sendMessage, entityId, user, agent, changeAgent } = useAIAssistant();

  const [prompt, setPrompt] = useState("");
  const [selectedItem, setSelectedItem] = useState<AIAgentOption>();

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const { accessibleBoards: boards } = useTaskBoardList({
    entityId,
    meId: user?.id || "",
  });

  const items: ListBoxItem<AIAgentOption>[] = useMemo(() => {
    if (!user || !entityId) return [];
    return [
      {
        id: "help",
        name: t({
          id: "ai-assistant-dialog-component.onboarding-help",
          defaultMessage: "Onboarding Help",
        }),
        icon: <OnbaordingIcon />,
        agent: {
          agent: AIAgentType.help as const,
        },
      },
      ...(hasRoleAccessToModule(user.role, AppModule.tasks)
        ? [
            {
              id: "my-tasks",
              name: t({
                id: "ai-assistant-dialog-component.my-tasks",
                defaultMessage: "My tasks",
              }),
              color: "#AB59E4",
              agent: {
                agent: AIAgentType.myTasks as const,
                entity_id: entityId,
                user_id: user.id,
              },
            },
            ...boards.map((board) => ({
              id: board.id,
              name: board.name,
              color: board.color,
              agent: {
                agent: AIAgentType.board as const,
                entity_id: entityId,
                board_id: board.id,
              },
            })),
          ]
        : []),
    ].map((item) => ({
      id: item.id,
      value: item,
    }));
  }, [boards, entityId, user, t]);

  useEffect(() => {
    const found = items.find((item) => isEqual(item.value.agent, agent));
    if (found) {
      setSelectedItem(found.value);
    }
  }, [agent, items]);

  const {
    textareaProps: { onInput: resizeTextarea },
  } = useAutoResizedTextarea(textareaRef);

  const handleSendMessage = useCallback(() => {
    sendMessage(prompt);
    setPrompt("");
  }, [prompt, sendMessage]);

  useEffect(() => {
    resizeTextarea();
  }, [prompt, resizeTextarea]);

  return (
    <div className="flex shrink-0 flex-col gap-2 overflow-hidden rounded-2xl bg-grey-100 p-4">
      <div className="flex items-center gap-4">
        <div className="jugl__custom-scrollbar max-h-[120px] w-full overflow-y-auto">
          <textarea
            className="h-full w-full resize-none overflow-hidden rounded-none border-none bg-grey-100 p-0 font-secondary text-sm leading-[140%] text-dark outline-none placeholder:text-dark-600"
            placeholder={t({
              id: "ai-assistant-dialog-component.ask-something",
              defaultMessage: "Ask something",
            })}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value.trimStart())}
            ref={textareaRef}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !!prompt.length) {
                handleSendMessage();
              }
            }}
            rows={1}
          />
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Tooltip
            renderTrigger={({ props, ref }) => (
              <PlainButton
                className="flex h-10 w-10 items-center justify-center rounded-full bg-primary transition-colors hover:bg-primary-600 disabled:bg-primary-300 disabled:hover:bg-primary-300"
                onClick={() => {
                  if (prompt.length === 0) return;
                  handleSendMessage();
                }}
                disabled={!prompt.length}
                ref={ref}
                {...props}
              >
                <SendIcon />
              </PlainButton>
            )}
          >
            {t({ id: "common.send", defaultMessage: "Send" })}
          </Tooltip>
        </div>
      </div>
      {selectedItem && (
        <ResourcePickerPopover
          placement="top-start"
          maxVisibleItems={5}
          hasSearch
          renderLabel={({ value }) => value.name}
          renderStartIcon={({ value }) =>
            value.color ? (
              <BoardAvatar color={value.color} name={value.name} size="sm" />
            ) : (
              value.icon
            )
          }
          defaultSelectedIds={[selectedItem.id]}
          className="font-secondary"
          renderTrigger={({ Trigger, triggerRef }) => (
            <Trigger
              as={InteractiveContainer}
              className="flex h-8 w-max max-w-full items-center justify-between gap-2 overflow-hidden rounded-md bg-[linear-gradient(to_top_right,#F3ECF8,#EAF2F8)] px-2 transition-colors hover:bg-[linear-gradient(to_top_right,#F0E0FD,#DBEEFD)]"
              ref={triggerRef}
            >
              {selectedItem.color ? (
                <BoardAvatar
                  color={selectedItem.color}
                  name={selectedItem.name}
                  size="sm"
                />
              ) : (
                selectedItem.icon
              )}
              <span className="lading-[21px] truncate font-secondary text-sm text-dark">
                {selectedItem.name}
              </span>
            </Trigger>
          )}
          items={items}
          selectionBehavior={{ mode: "single", canToggle: false }}
          onSelect={({ item, onClose }) => {
            changeAgent(item.value.agent);
            onClose();
          }}
        />
      )}
    </div>
  );
};
